import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import { AccountType, ProjectState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { CustomerAccountNotes } from '~components/Order/CustomerAccountNotes';
import { Account, AccountTypeahead } from '~hooks/useAccount/models';
import useAccount from '~hooks/useAccount/useAccount';
import { CompanyBasic } from '~hooks/useCompany/models';
import useDispatchNumberLabel from '~hooks/useDispatchNumberLabel';
import { Project } from '~hooks/useProjects/models';
import useProjects from '~hooks/useProjects/useProjects';
import useServiceClassOptions from '~hooks/useServiceClass/useServiceClassOptions';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

interface OrderCustomerDetailsV2Props {
  companyId?: string;
  isOrderNameRequired?: boolean;
  isServiceClassFieldVisible?: boolean;
  isServiceClassRequired?: boolean;
  project?: Project;
  tomlinsonOrderFormProjectFirstFeatureFlagEnabled?: boolean;
  tomlinsonOrderModalFeatureFlagEnabled?: boolean;
}

export default function OrderCustomerDetailsV2({
  companyId,
  isOrderNameRequired,
  isServiceClassFieldVisible,
  isServiceClassRequired,
  project,
  tomlinsonOrderFormProjectFirstFeatureFlagEnabled,
  tomlinsonOrderModalFeatureFlagEnabled,
}: OrderCustomerDetailsV2Props) {
  const { singular } = useDispatchNumberLabel();
  const form = useFormContext();
  const isEditing = Boolean(form.watch('id'));

  const { toasterStore, userStore } = useStores();
  const showContractNumber = userStore.companySettings.isEnabled('show_contract_number');
  const { createAccount, getAccountsByCompanyIdTypeahead } = useAccount();
  const { getProjectsByCompanyIdTypeahead } = useProjects();
  const { loadServiceClassOptions, serviceClassOptions } = useServiceClassOptions();

  const [tmpAccountName, setTmpAccountName] = useState<string>('');
  const [showChargeToAccountField, setShowChargeToAccountField] = useState(false);
  const onInputAccountName = (event: SyntheticEvent, value: string) => {
    setTmpAccountName(value);
  };

  useEffect(() => {
    if (tomlinsonOrderModalFeatureFlagEnabled) {
      return;
    }

    const chargeToAccount = form.getValues('chargeToAccount');

    if (chargeToAccount) {
      setShowChargeToAccountField(true);
    }
  }, [tomlinsonOrderModalFeatureFlagEnabled]);

  const watchedSelectedCompany = form.watch('company') as CompanyBasic | null;
  const watchedAccount = form.watch('account') as AccountTypeahead | null;

  const fetchAccountsByCompanyId = useCallback(
    async (additionalProps = {}) => {
      if (!companyId) {
        return [];
      }

      return await getAccountsByCompanyIdTypeahead({
        companyId,
        accountTypes: [AccountType.CUSTOMER],
        ...additionalProps,
      });
    },
    [companyId],
  );

  // Always show the user's company as the first option of the "charge to account" field
  const currentUserCompany = userStore.userCompanies?.[0];
  const fetchAccountsByCompanyIdWithUserCompany = useCallback(
    async (additionalProps = {}) => {
      if (!companyId) {
        return [];
      }

      const response = await getAccountsByCompanyIdTypeahead({
        companyId,
        accountTypes: [AccountType.CUSTOMER],
        ...additionalProps,
      });

      const isRequestingFirstPage =
        'cursor' in additionalProps && !additionalProps.cursor;

      if (response && isRequestingFirstPage && currentUserCompany) {
        const data = [
          { id: currentUserCompany.id, name: currentUserCompany.legalName },
          ...response.data,
        ];

        return { ...response, data };
      }

      return response;
    },
    [companyId, userStore],
  );

  const fetchProjectsByCompanyId = useCallback(
    async (additionalProps = {}) => {
      if (!companyId) {
        return [];
      }

      return await getProjectsByCompanyIdTypeahead({
        companyId: companyId,
        states: [ProjectState.ACTIVE],
        accountId: watchedAccount?.id,
        ...additionalProps,
      });
    },
    [companyId, watchedAccount?.id],
  );

  const createNewAccount = async () => {
    if (!watchedSelectedCompany?.id) {
      toasterStore.push(
        alert(t('form_validation_errors.no_user_company'), AlertTypes.error),
      );
      return;
    }

    try {
      const account = await createAccount({
        account: {
          name: tmpAccountName,
          accountTypes: [AccountType.CUSTOMER],
          company: watchedSelectedCompany as CompanyBasic,
          connectedCompany: watchedSelectedCompany as CompanyBasic,
        } as unknown as Account,
      });

      toasterStore.push(
        alert(t('account.account_created', { name: account.name }), AlertTypes.success),
      );
      form.setValue('account', account, { shouldDirty: false });
    } catch (error) {
      void error;
    }
  };

  const accountField = (
    <Grid size={{ xs: 6 }}>
      <AutocompleteAsyncFormField
        asyncCallback={fetchAccountsByCompanyId}
        control={form.control}
        disabled={!companyId || isEditing}
        errors={form.formState.errors}
        getLabel={(item) => item.name || ''}
        getValue={(item) => item.id}
        isRequired
        label={`${t('dispatch.order.customer_account')}`}
        name="account"
        onSelect={(option) => {
          form.setValue('project', null, { shouldDirty: false });

          return option;
        }}
        onInput={onInputAccountName}
        noOptionsText={
          <Chip
            size={'small'}
            variant={'filled'}
            color={'primary'}
            onClick={() => void createNewAccount()}
            sx={{ cursor: 'pointer' }}
            label={t('account.create_account')}
          />
        }
        dataUi={`page-sales-orders__order-customer-details-v2__account-input`}
      />
    </Grid>
  );

  // "Charge to Account" field value is to be defaulted from the selected project, but user can
  // still change this value. Upon order edition, chances are that the current "Charge to Account"
  // value is different from the one on the selected project. In this case, we should prevent this
  // value from being overriden after the full project data has been fetched, i.e. when editing an
  // order, "Charge to Account" field is defaulted from project only if the project has been
  // manually changed.
  const shouldDefaultChargeToAccountValueFromProject = useRef(isEditing ? false : true);

  const chargeToAccountField = (
    <Grid size={{ xs: 6 }}>
      <AutocompleteAsyncFormField
        asyncCallback={fetchAccountsByCompanyIdWithUserCompany}
        control={form.control}
        errors={form.formState.errors}
        getLabel={(item) => item.name || ''}
        getValue={(item) => item.name}
        label={`${t('order.form.charge_to_account')}`}
        name="chargeToAccount"
        onSelect={(option) => {
          return option.name;
        }}
        clearable
        dataUi={`page-sales-orders__order-customer-details-v2__charge-to-account-input`}
      />
    </Grid>
  );
  const projectField = (
    <Grid size={{ xs: 6 }}>
      <AutocompleteAsyncFormField
        asyncCallback={fetchProjectsByCompanyId}
        listboxWidth="750px"
        wrapOptions
        clearable
        control={form.control}
        disabled={!companyId}
        errors={form.formState.errors}
        getLabel={(item) =>
          item.externalId ? `${item.name} - ${item.externalId}` : item.name
        }
        getValue={(item) => item.id}
        isRequired={tomlinsonOrderFormProjectFirstFeatureFlagEnabled}
        label={`${t('order.form.associated_project_name')}`}
        name="project"
        // AutocompleteAsyncFormField calls onClear INSTEAD of react-hook-form's
        // onChange, so this write is the only thing that can dirty the field —
        // and the order form's sparse update payload only ships dirty fields.
        onClear={() => form.setValue('project', null, { shouldDirty: true })}
        onSelect={(option) => {
          if (isEditing) {
            shouldDefaultChargeToAccountValueFromProject.current = true;
          }

          return option;
        }}
        dataUi={`page-sales-orders__order-customer-details-v2__project-input`}
      />
    </Grid>
  );

  useEffect(() => {
    if (
      !shouldDefaultChargeToAccountValueFromProject.current ||
      tomlinsonOrderModalFeatureFlagEnabled
    ) {
      return;
    }

    const chargeToAccount = project?.chargeToAccount ?? null;

    // Inheriting from the project must dirty the field or the sparse update
    // payload drops it, but this effect also runs on mount when creating an
    // order — writing only on a real change keeps that mount write out of
    // `dirtyFields`.
    if (chargeToAccount !== (form.getValues('chargeToAccount') ?? null)) {
      form.setValue('chargeToAccount', chargeToAccount, { shouldDirty: true });
    }

    if (chargeToAccount) {
      setShowChargeToAccountField(true);
    }
  }, [isEditing, project?.chargeToAccount, tomlinsonOrderModalFeatureFlagEnabled]);

  useEffect(() => {
    if (!isEditing && project && isServiceClassFieldVisible) {
      form.setValue('serviceClass', project.serviceClass, { shouldDirty: false });
    }
  }, [isEditing, project, isServiceClassFieldVisible]);

  useEffect(() => {
    if (!companyId || !isServiceClassFieldVisible) return;
    void loadServiceClassOptions(companyId);
  }, [companyId, isServiceClassFieldVisible, loadServiceClassOptions]);

  return (
    <Grid container spacing={2}>
      {tomlinsonOrderFormProjectFirstFeatureFlagEnabled ? (
        <>
          {projectField}
          {accountField}
        </>
      ) : (
        <>
          {accountField}
          {projectField}
        </>
      )}
      <CustomerAccountNotes
        accountId={watchedAccount?.id}
        companyId={watchedSelectedCompany?.id}
      />
      {tomlinsonOrderModalFeatureFlagEnabled ? null : (
        <Grid size={{ xs: 12 }}>
          <PseudoLink
            action={() => setShowChargeToAccountField((state) => !state)}
            title={
              showChargeToAccountField
                ? t('order.form.hide_charge_to_account')
                : t('order.form.show_charge_to_account')
            }
            sx={{
              display: 'inline-block',
              lineHeight: '10px',
              mb: showChargeToAccountField ? 2 : 0,
            }}
          />

          {showChargeToAccountField ? chargeToAccountField : null}
        </Grid>
      )}
      <Grid size={{ xs: 6 }}>
        <TextFormField
          control={form.control}
          errors={form.formState.errors}
          isRequired={isOrderNameRequired}
          label={`${t('order.form.order_name')}`}
          name="name"
          dataUi={`page-sales-orders__order-customer-details-v2__name-input`}
        />
      </Grid>
      {showContractNumber && (
        <Grid size={{ xs: 6 }}>
          <TextFormField
            control={form.control}
            errors={form.formState.errors}
            label={`${t('order.form.contract_number')}`}
            name="contractNumber"
            dataUi={`page-sales-orders__order-customer-details-v2__contract-number-input`}
          />
        </Grid>
      )}
      <Grid size={{ xs: 6 }}>
        <TextFormField
          control={form.control}
          errors={form.formState.errors}
          label={singular('order.form.dispatch_number_full')}
          name="dispatchNumber"
          dataUi={`page-sales-orders__order-customer-details-v2__dispatch-number-input`}
        />
      </Grid>
      {isServiceClassFieldVisible && (
        <>
          <Grid size={{ xs: 6 }}>
            <AutocompleteFormField
              clearable
              control={form.control}
              errors={form.formState.errors}
              getLabel={(item) => item?.name ?? ''}
              getValue={(item) => item?.id ?? ''}
              isRequired={isServiceClassRequired && isServiceClassFieldVisible}
              label={`${t('form_fields.service_class')}`}
              list={serviceClassOptions}
              name="serviceClass"
              dataUi={`page-sales-orders__order-customer-details-v2__service-class-input`}
            />
          </Grid>

          {!showContractNumber && <Grid size={{ xs: 6 }} />}
        </>
      )}
      {tomlinsonOrderModalFeatureFlagEnabled ? null : (
        <>
          <Grid size={{ xs: 3 }}>
            <TextFormField
              control={form.control}
              errors={form.formState.errors}
              label={`${t('order.form.order_po')}`}
              name="poJobNumber"
              dataUi={`page-sales-orders__order-customer-details-v2__po-job-number-input`}
            />
          </Grid>

          <Grid size={{ xs: 3 }}>
            <TextFormField
              control={form.control}
              errors={form.formState.errors}
              label={`${t('order.form.order_zone')}`}
              name="zone"
              dataUi={`page-sales-orders__order-customer-details-v2__zone-input`}
            />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <TextFormField
              control={form.control}
              errors={form.formState.errors}
              label={`${t('form_fields.external_id')}`}
              name="externalId"
              dataUi={`page-sales-orders__order-customer-details-v2__external-id-input`}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
}
